import { roles } from '../../constants/roles';
import { DelegatedUsersTableProps } from './types';
import { InfoIcon } from '@g17eco/molecules/info-icon';
import { Button } from 'reactstrap';
import { QUESTION, SURVEY } from '@constants/terminology';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { StaffIcon } from '@g17eco/molecules/staff-icon';
import { hasAtLeastOneRoleLevel, isAssignedToAll, RoleLevel } from '@utils/delegation-users';
import { User } from '@api/survey-users';
import { DelegationUserText } from '@g17eco/molecules/delegation-user-text';
import { WorkgroupPermissions } from '@services/permissions/WorkgroupPermissions';
import { WorkgroupIcon } from '@g17eco/molecules/workgroup/WorkgroupIcon';
import { UtrvPermissions } from '@services/permissions/UtrvPermissions';
import { UtrvUserRole } from '@constants/user';
import { UtrvWorkgroup } from '@g17eco/types/universalTrackerValue';
import { MassDelegation } from '@g17eco/types/survey';

type UserRowProps = Pick<DelegatedUsersTableProps, 'utrvs' | 'handleRemoveUser' | 'isReadonly' | 'survey'> & {
  userRole: UtrvUserRole;
  user: User;
};

export const UserRow = (props: UserRowProps) => {
  const { userRole, user, utrvs, isReadonly } = props;
  const isFullCircle = isAssignedToAll(userRole, user, utrvs?.length || 0);
  const roleName = roles[userRole]?.shortName?.toLowerCase() || userRole;
  const tooltip = `Delegated as a ${roleName} for ${isFullCircle ? 'all' : 'some'} of the ${QUESTION.PLURAL} selected.`;

  return (
    <div className='d-flex align-items-center'>
      <CircleIcon isFull={isFullCircle} tooltip={tooltip} />
      <DelegationUserText user={user} />
      {user.workgroups?.map((workgroup) =>
        WorkgroupPermissions.checkHasUtrvsUserRoles({
          utrvs,
          survey: { workgroups: user.workgroups ?? [] },
          userId: user._id,
          roles: [userRole],
        }) ? (
          <SimpleTooltip text={workgroup.name} key={workgroup._id}>
            <WorkgroupIcon workgroup={workgroup} className='ms-2' />
          </SimpleTooltip>
        ) : null,
      )}
      {user.isStaff ? <StaffIcon /> : null}
      {isReadonly ? null : <Action {...props} />}
    </div>
  );
};

const CircleIcon = ({ isFull, tooltip }: { isFull: boolean; tooltip?: string }) => {
  const icon = isFull ? 'fa-circle' : 'fa-circle-half-stroke';

  return (
    <SimpleTooltip text={tooltip}>
      <i className={`text-ThemeIconDark mr-3 fa-solid ${icon}`}></i>
    </SimpleTooltip>
  );
};

const Action = ({ utrvs, userRole, user, handleRemoveUser, survey }: UserRowProps) => {
  if (
    UtrvPermissions.canUtrvExplicitBulk({ utrvs, survey, user, role: userRole, iteratorFn: (cb) => utrvs.some(cb) })
  ) {
    return (
      <Button
        color='link'
        className='mx-1 p-0 ml-auto mr-2'
        onClick={() => handleRemoveUser({ roles: [userRole], userIds: [user._id] })}
      >
        <i className='fas fa-times text-ThemeDangerMedium' />
      </Button>
    );
  }

  if (hasAtLeastOneRoleLevel([RoleLevel.Initiative], userRole, user)) {
    const text = `User has the ${roles[
      userRole
    ].shortName?.toLowerCase()} permission assigned to them at the platform level and cannot be removed from ${
      QUESTION.SINGULAR
    } level delegation.`;
    return (
      <div className='ml-auto mr-2'>
        <InfoIcon text={text} />
      </div>
    );
  }

  if (hasAtLeastOneRoleLevel([RoleLevel.Survey], userRole, user)) {
    const text = `User has the ${roles[userRole].shortName?.toLowerCase()} permission assigned to them at the ${
      SURVEY.SINGULAR
    } level and cannot be removed from ${QUESTION.SINGULAR} level delegation.`;
    return (
      <div className='ml-auto mr-2'>
        <InfoIcon text={text} />
      </div>
    );
  }

  return null;
};

export const WorkgroupRow = (
  props: Pick<DelegatedUsersTableProps, 'utrvs' | 'isReadonly'> & {
    workgroup: UtrvWorkgroup;
    userRole: UtrvUserRole;
    handleRemoveWorkgroup: (props: Pick<MassDelegation, 'roles' | 'workgroupIds'>) => void;
  },
) => {
  const { workgroup, utrvs, userRole, isReadonly, handleRemoveWorkgroup } = props;

  return (
    <div className='d-flex align-items-center justify-content-between'>
      <div className='d-flex align-items-center'>
        <CircleIcon isFull={workgroup.utrvIds.length === utrvs.length} />
        <WorkgroupIcon workgroup={workgroup} />
        {workgroup.name}
        <InfoIcon text={`Workgroup - ${workgroup.users.length} user(s)`} />
      </div>
      {isReadonly ? null : (
        <Button
          color='link'
          className='mx-1 p-0 ml-auto mr-2'
          onClick={() => handleRemoveWorkgroup({ roles: [userRole], workgroupIds: [workgroup._id] })}
        >
          <i className='fas fa-times text-ThemeDangerMedium' />
        </Button>
      )}
    </div>
  );
};